import { motion, MotionValue, useTransform } from "framer-motion";
import { ReactNode } from "react";
import StepBadge from "~/components/home/StepBadge";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

type Step = {
  title: string;
  description: ReactNode;
};

type Props = {
  step: Step;
  idx: number;
  align: "left" | "right";
  scrollYProgress: MotionValue<number>;
  segments: number;
  setRef: (el: HTMLDivElement | null) => void;
  setCardRef: (el: HTMLDivElement | null) => void;
};

export default function StepWithBadge({
  step,
  idx,
  align,
  scrollYProgress,
  segments,
  setRef,
  setCardRef,
}: Props) {
  const theme = useTheme();

  // Calculate approximate hit point for activating the badge and card
  const hitPoint = segments > 0 ? idx / segments : 1;
  const startAnim = Math.max(0, hitPoint - 0.15);
  const progress = useTransform(scrollYProgress, [startAnim, hitPoint], [0, 1]);

  const badgeBg = useTransform(
    progress,
    [0, 1],
    ["var(--mui-palette-text-primary)", "var(--mui-palette-primary-main)"],
  );
  const badgeColor = useTransform(
    progress,
    [0, 1],
    [
      "var(--mui-palette-background-paper)",
      "var(--mui-palette-primary-contrastText)",
    ],
  );

  const side =
    align === "left"
      ? ({ justify: "flex-end", textAlign: "right" } as const)
      : ({ justify: "flex-start", textAlign: "left" } as const);

  return (
    <Box
      sx={{
        width: { md: "45%" },
        pr: align === "left" ? { md: 4 } : 0,
        pl: align === "right" ? { md: 4 } : 0,
        transform: { md: `translateY(${idx % 2 ? 20 : 0}px)` },
        display: "flex",
        justifyContent: side.justify,
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: side.justify,
          alignItems: "center",
          gap: 2,
        }}
      >
        {align === "left" && (
          <StepBadge
            idx={idx}
            setRef={setRef}
            badgeBg={badgeBg}
            badgeColor={badgeColor}
          />
        )}

        <motion.div
          style={{ width: "100%", borderRadius: theme.shape.borderRadius }}
        >
          <Card
            ref={setCardRef}
            sx={{
              height: "100%",
              width: "100%",
              textAlign: side.textAlign,
              p: 3,
              boxShadow: 0,
              border: "none",
            }}
          >
            <CardContent>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
                {step.title}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  mt: 1
                }}>
                {step.description}
              </Typography>
            </CardContent>
          </Card>
        </motion.div>

        {align === "right" && (
          <StepBadge
            idx={idx}
            setRef={setRef}
            badgeBg={badgeBg}
            badgeColor={badgeColor}
          />
        )}
      </Box>
    </Box>
  );
}
